<template>
  <main class="capture-page">
    <section class="capture-card" aria-live="polite">
      <div class="brand-mark"><img src="/prexens-mark.svg" alt="" /></div>

      <template v-if="state === 'loading'">
        <span class="spinner" aria-hidden="true"></span>
        <p class="eyebrow">Ajout express</p>
        <h1>Ajout de l’annonce…</h1>
        <p class="description">
          Prexens analyse la page Yahoo Auctions et prépare votre rappel.
        </p>
      </template>

      <template v-else-if="state === 'success'">
        <span class="status-icon success" aria-hidden="true">✓</span>
        <p class="eyebrow">Ajout express</p>
        <h1>{{ successTitle }}</h1>
        <p class="description">{{ successMessage }}</p>
        <div class="actions">
          <button type="button" class="secondary" @click="closeWindow">
            Fermer cet onglet
          </button>
          <router-link class="primary" to="/offers"
            >Voir les annonces</router-link
          >
        </div>
      </template>

      <template v-else-if="state === 'authentication'">
        <span class="status-icon neutral" aria-hidden="true">↗</span>
        <p class="eyebrow">Connexion nécessaire</p>
        <h1>Connectez-vous à Prexens</h1>
        <p class="description">
          L’annonce sera ajoutée automatiquement juste après votre connexion.
        </p>
        <router-link class="primary single" :to="loginTarget"
          >Se connecter</router-link
        >
      </template>

      <template v-else>
        <span class="status-icon error" aria-hidden="true">!</span>
        <p class="eyebrow">Ajout impossible</p>
        <h1>{{ errorTitle }}</h1>
        <p class="description">{{ errorMessage }}</p>
        <div class="actions">
          <button type="button" class="secondary" @click="closeWindow">
            Fermer
          </button>
          <button type="button" class="primary" @click="captureOffer">
            Réessayer
          </button>
        </div>
      </template>
    </section>
  </main>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { startParse, waitForParse } from "@/api/parseJobs.js";
import { isTokenExpired } from "../../auth";

const PENDING_URL_KEY = "prexens:pending-capture-url";

export default {
  name: "CaptureOffer",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const state = ref("loading");
    const resultStatus = ref("");
    const errorTitle = ref("Cette annonce n’a pas pu être ajoutée");
    const errorMessage = ref("Vérifiez la page ouverte puis réessayez.");

    const urlFromHash = () => {
      const hash = route.hash.replace(/^#/, "");
      const params = new URLSearchParams(hash);
      return params.get("url") || "";
    };
    const capturedUrl = ref(
      urlFromHash() || sessionStorage.getItem(PENDING_URL_KEY) || ""
    );
    const loginTarget = computed(() => ({
      path: "/",
      query: { redirect: "/capture" },
    }));
    const successTitle = computed(() =>
      resultStatus.value === "alreadyTracked"
        ? "Annonce déjà suivie"
        : resultStatus.value === "pending"
          ? "Ajout lancé"
          : resultStatus.value === "timeout"
            ? "Analyse en cours"
            : "Annonce ajoutée"
    );
    const successMessage = computed(() =>
      resultStatus.value === "alreadyTracked"
        ? "Elle était déjà présente dans votre liste. Aucun doublon n’a été créé."
        : resultStatus.value === "pending"
          ? "Vous pouvez fermer cet onglet : l’analyse se poursuit. Laissez-le ouvert pour voir le résultat."
          : resultStatus.value === "timeout"
            ? "L’analyse prend plus de temps que prévu : l’annonce apparaîtra dans vos annonces suivies dès qu’elle sera terminée."
            : "Elle apparaît maintenant dans vos annonces suivies avec son rappel."
    );

    const isSupportedUrl = (value) => {
      try {
        const parsed = new URL(value);
        return (
          ["page.auctions.yahoo.co.jp", "auctions.yahoo.co.jp"].includes(
            parsed.hostname
          ) && parsed.pathname.includes("auction")
        );
      } catch {
        return false;
      }
    };

    const captureOffer = async () => {
      if (!isSupportedUrl(capturedUrl.value)) {
        state.value = "error";
        errorTitle.value = "Page Yahoo Auctions non reconnue";
        errorMessage.value =
          "Utilisez ce favori depuis la page détaillée d’une annonce Yahoo Auctions.";
        return;
      }
      sessionStorage.setItem(PENDING_URL_KEY, capturedUrl.value);
      const token = localStorage.getItem("access_token");
      if (!token || isTokenExpired(token)) {
        state.value = "authentication";
        return;
      }
      state.value = "loading";
      try {
        // Ajout lancé : l'onglet peut être fermé tout de suite, l'analyse continue
        // côté API. S'il reste ouvert, il affiche le résultat final.
        const jobId = await startParse(capturedUrl.value, true);
        sessionStorage.removeItem(PENDING_URL_KEY);
        resultStatus.value = "pending";
        state.value = "success";

        const outcome = await waitForParse(jobId);
        if (outcome.status === "Failed") {
          state.value = "error";
          errorTitle.value = "Cette annonce n’a pas pu être ajoutée";
          errorMessage.value =
            outcome.error || "L’analyse de la page a échoué. Réessayez dans quelques instants.";
          return;
        }
        resultStatus.value = outcome.status === "Completed" ? "created" : "timeout";
      } catch (error) {
        if (error.response?.status === 401) {
          state.value = "authentication";
          return;
        }
        if (error.response?.status === 409) {
          resultStatus.value = "alreadyTracked";
          sessionStorage.removeItem(PENDING_URL_KEY);
          state.value = "success";
          return;
        }
        state.value = "error";
        errorTitle.value =
          error.response?.status === 404
            ? "Annonce introuvable"
            : "Cette annonce n’a pas pu être ajoutée";
        errorMessage.value =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Le service est momentanément indisponible. Votre URL est conservée pour une nouvelle tentative.";
      }
    };

    const closeWindow = () => {
      window.close();
      window.setTimeout(() => router.push("/offers"), 150);
    };

    onMounted(captureOffer);
    return {
      captureOffer,
      closeWindow,
      errorMessage,
      errorTitle,
      loginTarget,
      state,
      successMessage,
      successTitle,
    };
  },
};
</script>

<style scoped>
.capture-page {
  display: grid;
  min-height: 100vh;
  padding: 24px;
  place-items: center;
  background: #f5f7fa;
  color: #172033;
  font-family: "DM Sans", sans-serif;
}
.capture-card {
  width: min(500px, 100%);
  padding: 40px;
  border: 1px solid #e1e6ed;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 18px 55px rgba(15, 23, 42, 0.09);
  text-align: center;
}
.brand-mark {
  width: 44px;
  height: 44px;
  margin: 0 auto 26px;
  overflow: hidden;
  border-radius: 12px;
}
.brand-mark img {
  display: block;
  width: 100%;
  height: 100%;
}
.spinner {
  display: block;
  width: 34px;
  height: 34px;
  margin: 0 auto 22px;
  border: 3px solid #dce5e2;
  border-top-color: #168a64;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
.status-icon {
  display: grid;
  width: 42px;
  height: 42px;
  margin: 0 auto 20px;
  place-items: center;
  border-radius: 50%;
  font-size: 20px;
  font-weight: 800;
}
.status-icon.success {
  background: #e8f7f1;
  color: #168a64;
}
.status-icon.neutral {
  background: #edf3f8;
  color: #52708f;
}
.status-icon.error {
  background: #fff0ef;
  color: #b64842;
}
.eyebrow {
  margin: 0 0 7px;
  color: #168a64;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
h1 {
  margin: 0;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: 25px;
  line-height: 1.25;
  letter-spacing: -0.03em;
}
.description {
  max-width: 390px;
  margin: 10px auto 0;
  color: #6c7889;
  font-size: 13px;
  line-height: 1.6;
}
.actions {
  display: flex;
  gap: 10px;
  margin-top: 28px;
}
.actions button,
.actions a,
.primary.single {
  display: inline-flex;
  min-height: 40px;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
}
.secondary {
  border: 1px solid #d3dbe4;
  background: #fff;
  color: #536073;
}
.primary {
  border: 1px solid #172235;
  background: #172235;
  color: #fff;
}
.primary.single {
  width: 100%;
  margin-top: 28px;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (max-width: 520px) {
  .capture-page {
    padding: 14px;
  }
  .capture-card {
    padding: 30px 20px;
  }
  .actions {
    flex-direction: column-reverse;
  }
  .actions button,
  .actions a {
    width: 100%;
  }
}
</style>
